import React, { useContext, useEffect, useState } from 'react';

import { Button, Grid, Typography } from '@mui/material';
import SideBar from '../../uiParts/SideBar';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomTextfield from '../../uiParts/CustomTextfield';
import {
  gray100,
  primaryButtonBackgroundColor
} from '../../../constants/Colors';

import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { response } from '../../../api/db';
import { applyChangeEmail, getCustomerInfo } from '../../../api/db/user';
import { AuthContext } from '../../../contexts/auth';
import { common } from '../../../modules/error';
import { EmailChangeType } from '../../../validation/user/userType';
import { emailChangeSchema } from '../../../validation/user';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  container: {
    '@media screen and (min-width:961px)': {
      ml: sideBarWidth,
      height: '100vh'
    },
    '@media screen and (max-width:960px)': {
      mt: spHeaderHeight
    },
    direction: 'column',
    justifyContent: 'center',
    height: `calc(100vh - ${spHeaderHeight})`
  },
  body: {
    '@media screen and (max-width:600px)': {
      width: '340px',
      mt: '24px'
    },
    textAlign: 'left',
    maxWidth: '560px',
    width: '95%',
    mt: '32px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    fontSize: '32px',
    fontWeight: 500,
    mb: '24px',
    color: gray100
  },
  text: {
    fontSize: '16px',
    mb: '48px',
    color: gray100
  },
  button: {
    width: '100%',
    ...primaryButtonBackgroundColor
  }
};

const EmailChange: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const { isLoggedIn } = useContext(AuthContext);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch
  } = useForm<EmailChangeType>({
    resolver: yupResolver(emailChangeSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return watch('newEmail') === undefined || '' || Boolean(errors.newEmail);
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      await applyChangeEmail(watch('newEmail'));
      navigate('/account/email/sent');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (!isLoggedIn) {
        navigate('/auth/login');
        return;
      }
      try {
        // ログイン中のユーザーを取得
        const response = await getCustomerInfo();
        const user = response.data;
        reset({ prevEmail: user.email });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          setErrorText(errorResponse.data.message);
          setOpen(true);
        } else {
          setErrorText(common);
          setOpen(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <CustomHead title="メールアドレス変更 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid container>
        <SideBar />
        <Grid container sx={styles.container}>
          <Grid item sx={styles.body}>
            <Typography sx={styles.title}>メールアドレス変更</Typography>
            <Typography sx={styles.text}>
              メールアドレスの変更にはメールによる認証が必要となります。
              <br />
              認証用URLが記載されたメールを送信しますので、
              変更後のアドレスを以下に入力してください。
            </Typography>

            <Controller
              control={control}
              name="prevEmail"
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  disabled={true}
                  labelText="現在のメールアドレス"
                  formMb="24px"
                  placeholder="info@sample.com"
                  readOnly={true}
                  width="100%"
                  sx={{ maxWidth: '560px' }}
                />
              )}
            />

            <Controller
              control={control}
              name="newEmail"
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.newEmail)}
                  helperText={errors.newEmail?.message}
                  labelText="新しいメールアドレス"
                  requiredText="必須"
                  formMb="40px"
                  width="100%"
                  sx={{ maxWidth: '560px' }}
                />
              )}
            />

            <Button
              disabled={buttonDisabledFlag()}
              variant="contained"
              sx={styles.button}
              onClick={() => {
                setFirstSubmit(true);
                handleSubmit(async () => await onSubmit())();
              }}
            >
              認証メールを送信する
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <CustomSnackbar
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        open={open}
        text={errorText}
      />
    </>
  );
};

export default EmailChange;
